import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Download, Trash2, Save as SaveIcon, GraduationCap, FileSpreadsheet, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';

// Interface matching the DB schema extension
interface Preference {
    id: string;
    student_id: string;
    sube: string;
    gorusme_status: boolean;
    missing_documents: string;
    adi: string;
    soyadi: string;
    email: string;

    // Universitaly
    universitaly_username: string;
    universitaly_pass: string;

    // University Application
    university: string;
    department: string;
    uni_username: string;
    uni_pass: string;
    status: string; // "durum"

    // Payment & Result
    basvuru_ucreti: string;
    sonuc: string;

    // Requirements
    gerekli_sinavlar: string;
    deadline: string;
    notlar: string;

}

// Column Definition for dynamic headers and resizing
const INITIAL_COLUMNS = [
    { key: 'sube', label: 'ŞUBE', width: 80 },
    { key: 'gorusme_status', label: 'Görüşme', width: 70 },
    { key: 'missing_documents', label: 'Eksik Evraklar', width: 150 },
    { key: 'adi', label: 'ADI', width: 100 },
    { key: 'soyadi', label: 'SOYADI', width: 100 },
    { key: 'universitaly_username', label: 'Universitaly', width: 120 },
    { key: 'universitaly_pass', label: 'Universitaly Şifre', width: 100 },
    { key: 'university', label: 'ÜNİVERSİTE', width: 150 },
    { key: 'department', label: 'BÖLÜM', width: 150 },
    { key: 'uni_username', label: 'KULLANICI ADI', width: 120 },
    { key: 'uni_pass', label: 'ŞİFRE', width: 100 },
    { key: 'status', label: 'DURUM', width: 100 },
    { key: 'basvuru_ucreti', label: 'BAŞVURU ÜCRETİ', width: 120 },
    { key: 'sonuc', label: 'SONUÇ', width: 100 },
    { key: 'gerekli_sinavlar', label: 'Gerekli Sınavlar', width: 150 },
    { key: 'deadline', label: 'DEADLINE', width: 100 },
    { key: 'notlar', label: 'NOTLAR', width: 200 },
];

const PreferencesPage: React.FC = () => {
    const [preferences, setPreferences] = useState<Preference[]>([]);
    const [currentUserBranchName, setCurrentUserBranchName] = useState<string>('');
    const [universityList, setUniversityList] = useState<{ id: string, name: string }[]>([]);

    const [searchTerm, setSearchTerm] = useState('');

    const [tempRows, setTempRows] = useState<any[]>([{ id: `temp_${Date.now()}` }]);

    const handleTempUpdate = (id: string, field: string, value: any) => {
        setTempRows(prev => {
            const newRows = prev.map(row => row.id === id ? { ...row, [field]: value } : row);

            // Check if last row is started (i.e., has some meaningful data)
            const lastRow = newRows[newRows.length - 1];
            if (lastRow.id === id) { // Only if we're editing the last row
                const isStarted = lastRow.adi?.length > 0 || lastRow.soyadi?.length > 0 || lastRow.university?.length > 0;
                if (isStarted) {
                    return [...newRows, { id: `temp_${Date.now()}_${Math.random()}` }];
                }
            }
            return newRows;
        });
    };

    // Load temp rows from local storage on mount
    useEffect(() => {
        const saved = localStorage.getItem('preferences_temp_rows');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setTempRows(parsed);
                }
            } catch (e) {
                console.error('Failed to load temp rows', e);
            }
        }
    }, []);

    // Save temp rows to local storage whenever they change
    useEffect(() => {
        if (tempRows.length > 0) {
            localStorage.setItem('preferences_temp_rows', JSON.stringify(tempRows));
        }
    }, [tempRows]);

    const [savingRows, setSavingRows] = useState<Set<string>>(new Set());

    const checkAutoSave = (row: any) => {
        // Auto-save disabled as per user request
    };

    const saveTempRow = async (row: any) => {
        if (savingRows.has(row.id)) return; // Already saving

        if (!row.adi || !row.soyadi) {
            // Minimal requirement: Name and Surname
            return;
        }

        try {
            setSavingRows(prev => new Set(prev).add(row.id));
            const email = row.email || `${row.adi?.toLowerCase()}.${row.soyadi?.toLowerCase()}.${Date.now()}@placeholder.com`.replace(/\s/g, '');

            // Applications belong to operation_students in the database schema.
            const { data: studentData, error: studentError } = await supabase
                .from('operation_students')
                .insert({
                    first_name: row.adi,
                    last_name: row.soyadi,
                    email: email,
                    branch_id: user?.branchId,
                    created_by: user?.id
                })
                .select()
                .single();

            if (studentError) throw studentError;

            // 2. Create Application
            // Find university ID if selected
            const selectedUni = universityList.find(u => u.name === row.university);
            const universityId = selectedUni ? selectedUni.id : null;

            const payload = {
                student_id: studentData.id,
                university_id: universityId, // Link to university table
                university: row.university,
                program: row.department,
                status: row.status || 'Bekliyor',
                interview_status: row.gorusme_status,
                missing_documents: row.missing_documents,
                universitaly_username: row.universitaly_username,
                universitaly_password: row.universitaly_pass,
                portal_username: row.uni_username,
                portal_password: row.uni_pass,
                application_fee_details: row.basvuru_ucreti,
                result_status: row.sonuc,
                exam_requirements: row.gerekli_sinavlar,
                deadline: row.deadline,
                notes: row.notlar,
                submitted_at: new Date().toISOString()
            };

            const { error: appError } = await supabase
                .from('university_applications')
                .insert(payload);

            if (appError) throw appError;

            // Success: Remove temp row and refresh
            setTempRows(prev => prev.filter(r => r.id !== row.id));
            fetchPreferences();
            alert('Kayıt başarıyla oluşturuldu.');

            // Note: We don't remove the last empty row, so user can continue adding
        } catch (e: any) {
            console.error('Save error:', e);
            alert('Kaydetme hatası: ' + e.message);
        } finally {
            setSavingRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(row.id);
                return newSet;
            });
        }
    };

    const removeTempRow = (id: string) => {
        setTempRows(prev => {
            // If it's the last one and empty, maybe don't remove? 
            // But if user clicks trash, they want it gone.
            // If we remove the last one, we should probably ensure there's always one empty at the end?
            // But let's just delete it.
            const filtered = prev.filter(r => r.id !== id);
            if (filtered.length === 0) {
                return [{ id: `temp_${Date.now()}` }];
            }
            return filtered;
        });
    };

    // Column Resizing State
    const [columns, setColumns] = useState(INITIAL_COLUMNS);
    const [resizingCol, setResizingCol] = useState<string | null>(null);
    const resizingRef = useRef<{ startX: number; startWidth: number } | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        fetchPreferences();
    }, []);

    // Load saved column widths on mount or user change
    useEffect(() => {
        if (user?.id) {
            const savedColumnsStr = localStorage.getItem(`preferences_table_columns_${user.id}`);
            if (savedColumnsStr) {
                try {
                    const savedColumns = JSON.parse(savedColumnsStr);
                    // Merge saved widths with INITIAL_COLUMNS structure to avoid showing deprecated columns
                    const newColumns = INITIAL_COLUMNS.map(col => {
                        const savedCol = savedColumns.find((s: any) => s.key === col.key);
                        return savedCol ? { ...col, width: savedCol.width } : col;
                    });
                    setColumns(newColumns);
                } catch (e) {
                    console.error('Failed to parse saved columns', e);
                }
            }
        }
    }, [user?.id]);

    // Resize Handlers
    const handleMouseDown = (e: React.MouseEvent, key: string, currentWidth: number) => {
        e.preventDefault();
        setResizingCol(key);
        resizingRef.current = { startX: e.clientX, startWidth: currentWidth };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!resizingRef.current || !resizingCol) return;
        const diff = e.clientX - resizingRef.current.startX;
        const newWidth = Math.max(50, resizingRef.current.startWidth + diff);

        setColumns(cols => cols.map(col =>
            col.key === resizingCol
                ? { ...col, width: newWidth }
                : col
        ));
    }, [resizingCol]);

    const handleMouseUp = useCallback(() => {
        setResizingCol(null);
        resizingRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Save to localStorage
        if (user?.id) {
            // We need to use the functional update form of setColumns to access the latest state, 
            // OR use a mechanism to get the current state. 
            // Since handleMouseUp is a closure, 'columns' might be stale if we just verify it here directly in a naive way.
            // However, handleMouseMove updates the state. 
            // The best way to save the *latest* state is to use an effect that triggers on columns change,
            // or just save it here by accessing the formatted state if possible. 
            // Better yet, let's use a separate useEffect for autosaving or save in this handler using the state setter callback to get latest, then saving.
            // Actually, simply saving in a useEffect dependent on [columns] might be too frequent (on every pixel drag).
            // So we save on mouse up. But we need the *latest* columns.
            setColumns(currentCols => {
                localStorage.setItem(`preferences_table_columns_${user.id}`, JSON.stringify(currentCols));
                return currentCols;
            });
        }
    }, [handleMouseMove, user?.id]);

    // Clean up event listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);


    const fetchPreferences = async () => {
        try {
            // Fetch branches first for mapping
            const branchesQuery = supabase.from('branches').select('id, name');
            const { data: branchesData } = user?.branchId
                ? await branchesQuery.eq('id', user.branchId)
                : { data: [] };
            const branchMap = new Map((branchesData || []).map(b => [b.id, b.name]));

            // Fetch universities
            const { data: universitiesData } = await supabase.from('universities').select('id, name').order('name');
            if (universitiesData) setUniversityList(universitiesData);

            // Find current user's branch name
            if (user?.branchId && branchMap.has(user.branchId)) {
                setCurrentUserBranchName(branchMap.get(user.branchId)!);
            }

            const query = supabase
                .from('university_applications')
                .select(`
                    id,
                    student_id,
                    student_name,
                    student_surname,
                    university,
                    program,
                    status,
                    deadline,
                    notes,
                    submitted_at,
                    
                    missing_documents,
                    universitaly_username,
                    universitaly_password,
                    portal_username,
                    portal_password,
                    interview_status,
                    application_fee_details,
                    result_status,
                    exam_requirements
                `)
                .eq('branch_id', user?.branchId)
                .order('created_at', { ascending: false });

            const { data, error } = await query;

            if (error) throw error;

            // Do not use a PostgREST embedded relation here. Some existing
            // environments were deployed without the FK, so resolve students
            // explicitly and keep the screen usable while the migration lands.
            const studentIds = [...new Set((data || []).map((app: any) => app.student_id).filter(Boolean))];
            const operationStudentMap = new Map<string, any>();

            if (studentIds.length > 0) {
                const [{ data: operationStudents, error: operationStudentsError }, { data: legacyStudents }] = await Promise.all([
                    supabase
                        .from('operation_students')
                        .select('id, first_name, last_name, email, branch_id')
                        .in('id', studentIds),
                    supabase
                        .from('students')
                        .select('id, first_name, last_name, email, branch_id')
                        .in('id', studentIds)
                ]);

                if (operationStudentsError) throw operationStudentsError;
                (legacyStudents || []).forEach((student: any) => operationStudentMap.set(student.id, student));
                (operationStudents || []).forEach((student: any) => operationStudentMap.set(student.id, student));
            }

            const mapped = (data || []).flatMap((app: any) => {
                const student = operationStudentMap.get(app.student_id);
                if (user?.branchId && student?.branch_id !== user.branchId) return [];

                return [{
                    id: app.id,
                    student_id: app.student_id,
                    sube: (student?.branch_id && branchMap.has(student.branch_id)) ? branchMap.get(student.branch_id)! : 'Merkez',
                    gorusme_status: app.interview_status || false,
                    missing_documents: app.missing_documents || '',
                    adi: student?.first_name || app.student_name || '',
                    soyadi: student?.last_name || app.student_surname || '',
                    email: student?.email || '',
                    universitaly_username: app.universitaly_username || '',
                    universitaly_pass: app.universitaly_password || '',
                    university: app.university || '',
                    department: app.program || '',
                    uni_username: app.portal_username || '',
                    uni_pass: app.portal_password || '',
                    status: app.status || 'Bekliyor',
                    basvuru_ucreti: app.application_fee_details || (app.application_fee ? `${app.application_fee} EUR` : ''),
                    sonuc: app.result_status || '',
                    gerekli_sinavlar: app.exam_requirements || '',
                    deadline: app.deadline || '',
                    notlar: app.notes || ''
                }];
            });

            setPreferences(mapped);

        } catch (error: any) {
            console.error('Error fetching preferences:', error);
            alert('Veri yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        }
    };

    const handleUpdate = async (id: string, field: string, value: any) => {
        setPreferences(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));

        const dbFieldMap: Record<string, string> = {
            'gorusme_status': 'interview_status',
            'missing_documents': 'missing_documents',
            'universitaly_username': 'universitaly_username',
            'universitaly_pass': 'universitaly_password',
            'university': 'university',
            'department': 'program',
            'uni_username': 'portal_username',
            'uni_pass': 'portal_password',
            'status': 'status',
            'basvuru_ucreti': 'application_fee_details',
            'sonuc': 'result_status',
            'gerekli_sinavlar': 'exam_requirements',
            'deadline': 'deadline',
            'notlar': 'notes'
        };

        const dbField = dbFieldMap[field];
        if (!dbField) return;

        try {
            const updatePayload: any = { [dbField]: value };

            // If updating university name, also update university_id
            if (field === 'university') {
                const selectedUni = universityList.find(u => u.name === value);
                updatePayload['university_id'] = selectedUni ? selectedUni.id : null;
            }

            await supabase.from('university_applications').update(updatePayload).eq('id', id);
        } catch (err) {
            console.error('Update failed:', err);
        }
    };



    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        await supabase.from('university_applications').delete().eq('id', id);
        fetchPreferences();
    };

    const downloadExcel = () => {
        const ws = XLSX.utils.json_to_sheet(preferences.map(p => ({ ...p, id: undefined, student_id: undefined })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tercihler");
        XLSX.writeFile(wb, "Tercihler.xlsx");
    };

    const renderInput = (row: any, field: string, placeholder = '', type = 'text') => {
        const isTemp = row.id.toString().startsWith('temp_');
        return (
            <input
                type={type}
                className={`w-full bg-transparent border-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-1 text-xs ${isTemp ? 'placeholder-gray-400 italic' : ''}`}
                value={row[field] || ''}
                placeholder={placeholder}
                onChange={(e) => {
                    const val = e.target.value;
                    if (isTemp) {
                        handleTempUpdate(row.id, field, val);
                    } else {
                        setPreferences(prev => prev.map(p => p.id === row.id ? { ...p, [field]: val } : p));
                    }
                }}
                onBlur={(e) => {
                    if (!isTemp) {
                        handleUpdate(row.id, field, e.target.value);
                    } else {
                        checkAutoSave(row);
                    }
                }}
            />
        );
    };

    const renderCell = (row: any, colKey: string) => {
        const isTemp = row.id.toString().startsWith('temp_');
        const updateRow = (field: string, val: any) => {
            if (isTemp) {
                handleTempUpdate(row.id, field, val);
            } else {
                handleUpdate(row.id, field, val);
            }
        };

        switch (colKey) {
            case 'gorusme_status':
                return (
                    <div className="flex justify-center">
                        <input
                            type="checkbox"
                            checked={row.gorusme_status || false}
                            onChange={(e) => updateRow('gorusme_status', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                    </div>
                );
            case 'status':
                return (
                    <select
                        value={row.status || 'Bekliyor'}
                        onChange={(e) => updateRow('status', e.target.value)}
                        className="bg-transparent border-none text-[10px] font-bold uppercase focus:ring-0 w-full"
                    >
                        <option value="Bekliyor">Bekliyor</option>
                        <option value="Başvuru Yapıldı">Başvuru Yapıldı</option>
                        <option value="Kabul">Kabul</option>
                        <option value="Red">Red</option>
                        <option value="Tamamlandı">Tamamlandı</option>
                    </select>
                );
            case 'deadline':
                return renderInput(row, colKey, '', 'date');
            case 'university':
                if (isTemp) {
                    return (
                        <select
                            value={row.university || ''}
                            onChange={(e) => {
                                handleTempUpdate(row.id, 'university', e.target.value);
                                // Also trigger auto-save check if name/surname are filled
                                checkAutoSave({ ...row, university: e.target.value });
                            }}
                            className="w-full bg-transparent border-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-1 text-xs"
                        >
                            <option value="">Seçiniz</option>
                            {universityList.map(uni => (
                                <option key={uni.id} value={uni.name}>{uni.name}</option>
                            ))}
                        </select>
                    );
                }
                // For existing rows, let's allow editing via select as well
                return (
                    <select
                        value={row.university || ''}
                        onChange={(e) => handleUpdate(row.id, 'university', e.target.value)}
                        className="w-full bg-transparent border-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-1 text-xs"
                    >
                        <option value="">Seçiniz</option>
                        {universityList.map(uni => (
                            <option key={uni.id} value={uni.name}>{uni.name}</option>
                        ))}
                    </select>
                );
            case 'sube':
            case 'adi':
            case 'soyadi':
                // For temp rows, these should be editable for inputs
                if (isTemp) {
                    if (colKey === 'sube') {
                        return <span className="px-1 text-gray-500 italic">{currentUserBranchName || 'Merkez'}</span>;
                    }
                    return renderInput(row, colKey, colKey.toUpperCase());
                }
                if (colKey === 'adi' || colKey === 'soyadi') return <span className="font-bold px-1">{(row as any)[colKey]}</span>;
                return <span className="px-1">{(row as any)[colKey]}</span>;
            default:
                return renderInput(row, colKey, (row as any)[colKey]);
        }
    };

    return (
        <div className="p-6 md:p-8 space-y-6">
            <div className="operations-page-hero">
                <div className="operations-page-hero__icon">
                    <GraduationCap className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="operations-eyebrow">Başvuru yönetimi</p>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-950 dark:text-white tracking-tight">
                        Üniversite tercihleri
                    </h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Öğrencilerin başvuru, belge ve sonuç süreçlerini tek ekrandan yönetin.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-end">
                    <div className="operations-count-pill">
                        <FileSpreadsheet className="w-4 h-4" />
                        <span>{preferences.length} başvuru</span>
                    </div>
                    <button
                        onClick={downloadExcel}
                        className="operations-primary-button"
                    >
                        <Download className="w-4 h-4" />
                        Excel'e aktar
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="operations-toolbar">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Öğrenci, üniversite veya bölüm ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                </div>
                <div className="hidden lg:flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    <Plus className="w-4 h-4 text-blue-600" />
                    Yeni kayıt için mavi satıra yazın
                </div>
            </div>

            {/* Table */}
            <div className="operations-data-card bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="overflow-auto relative" style={{ maxHeight: '75vh' }}>
                    <table className="text-xs text-left whitespace-nowrap min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 font-medium border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
                            <tr>
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        className="relative px-2 py-3 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 last:border-none group select-none"
                                        style={{ width: col.width, minWidth: col.width }}
                                    >
                                        <div className="flex items-center justify-between h-full">
                                            <span className="truncate">{col.label}</span>
                                        </div>
                                        {/* Resize Handle */}
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-blue-200 z-30"
                                            onMouseDown={(e) => handleMouseDown(e, col.key, col.width)}
                                        />
                                    </th>
                                ))}
                                <th className="px-2 py-3 w-10 text-center sticky right-0 bg-gray-50 z-20">Sil</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {preferences.filter(p =>
                                (p.adi + ' ' + p.soyadi).toLowerCase().includes(searchTerm.toLowerCase()) ||
                                p.university.toLowerCase().includes(searchTerm.toLowerCase())
                            ).map((row) => (
                                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                    {columns.map(col => (
                                        <td key={col.key} className="px-2 py-1 border-r border-gray-100 dark:border-gray-800 last:border-none max-w-0 truncate" style={{ width: col.width }}>
                                            {renderCell(row, col.key)}
                                        </td>
                                    ))}
                                    <td className="px-2 py-1 text-center sticky right-0 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 z-10">
                                        <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:text-red-700">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {/* Temp Rows / New Entry Rows */}
                            {!searchTerm && tempRows.map((row) => (
                                <tr key={row.id} className="bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-900/10 dark:hover:bg-blue-900/20 transition-colors border-2 border-dashed border-blue-200 dark:border-blue-800">
                                    {columns.map(col => (
                                        <td key={col.key} className="px-2 py-1 border-r border-blue-100 dark:border-blue-800/50 last:border-none max-w-0 truncate" style={{ width: col.width }}>
                                            {renderCell(row, col.key)}
                                        </td>
                                    ))}
                                    <td className="px-2 py-1 text-center sticky right-0 bg-blue-50/50 dark:bg-gray-900 border-l border-blue-100 dark:border-blue-800 z-10">
                                        <div className="flex items-center justify-center gap-2">
                                            <button onClick={() => saveTempRow(row)} className="text-green-600 hover:text-green-700 bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm">
                                                <SaveIcon className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => removeTempRow(row.id)} className="text-red-400 hover:text-red-600">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ADD MODAL with Exact Requested Fields */}
            {/* ADD MODAL REMOVED */}
        </div>
    );
};

export default PreferencesPage;
